import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { Check, X } from "lucide-react";
import { AuthProvider, useAuth } from "@/lib/auth";
import { ThemeProvider, useTheme } from "@/lib/theme";
import { api } from "@/lib/api";
import { loadSiteUrl } from "@/lib/site";
import Landing from "@/pages/Landing";
import { AuthPage, ForgotPassword, ResetPassword } from "@/pages/Auth";
import FaqPage from "@/pages/FaqPage";
import Showcase from "@/pages/Showcase";
import ClientPortalPage from "@/pages/ClientPortal";
import LegalPage from "@/pages/Legal";
import AppShell from "@/pages/AppShell";
import Welcome from "@/pages/Welcome";
import { isTrialEligible } from "@/components/sg/PayButtons";

function Loading() {
  return <div className="min-h-screen bg-background flex items-center justify-center text-muted-foreground">Loading…</div>;
}

function RequireAuth({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/auth" replace />;
  return children;
}

function GuestOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (user) {
    const fresh = user.created_at && Date.now() - new Date(user.created_at).getTime() < 10 * 60 * 1000;
    return <Navigate to={fresh && isTrialEligible(user) ? "/welcome" : "/app"} replace />;
  }
  return children;
}

function PaymentResult({ success }) {
  const nav = useNavigate();
  const { refresh } = useAuth();
  useEffect(() => {
    const qs = new URLSearchParams(window.location.search);
    const sid = qs.get("session_id") || qs.get("subscription_id");
    if (!success || !sid) { const t = setTimeout(() => nav("/app"), 2500); return () => clearTimeout(t); }
    let n = 0;
    const t = setInterval(async () => {
      n += 1;
      try {
        const r = await api.get(`/payments/status/${sid}`);
        if (r.data.payment_status === "paid") {
          clearInterval(t); const u = await refresh();
          toast.success(u.trial_ends_at ? "Your 7-day Super Premium trial has started!" : "Payment confirmed — your plan is active."); nav("/app");
        }
      } catch { /* keep polling */ }
      if (n > 15) { clearInterval(t); toast.message("Still confirming your payment. Your plan will update shortly."); nav("/app"); }
    }, 2000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success]);
  return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center" data-testid="payment-result">
      <div className="text-center">
        {success ? <Check className="h-10 w-10 text-emerald-600 mx-auto mb-3" /> : <X className="h-10 w-10 text-red-600 mx-auto mb-3" />}
        <div className="font-display text-xl font-bold">{success ? "Confirming your payment…" : "Checkout cancelled — no charge made"}</div>
        <div className="text-sm text-muted-foreground mt-2">Taking you back to ScopeGuard.</div>
      </div>
    </div>
  );
}

function ThemeSync() {
  const { user } = useAuth();
  const { setTheme } = useTheme();
  const uid = user ? user.id : null;
  useEffect(() => {
    if (uid && user.theme) setTheme(user.theme);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);
  return null;
}

function ScrollToHash() {
  const { hash, pathname } = useLocation();
  useEffect(() => {
    if (hash) setTimeout(() => document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" }), 50);
    else window.scrollTo({ top: 0 });
  }, [hash, pathname]);
  return null;
}

function Themed() {
  const { theme } = useTheme();
  return <Toaster richColors position="top-right" theme={theme} />;
}

export default function Root() {
  useEffect(() => { loadSiteUrl(); }, []);
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Themed />
          <ThemeSync />
          <ScrollToHash />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/auth" element={<GuestOnly><AuthPage /></GuestOnly>} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/faq" element={<FaqPage />} />
            <Route path="/legal/:doc" element={<LegalPage />} />
            <Route path="/c/:slug" element={<Showcase />} />
            <Route path="/portal/:token" element={<ClientPortalPage />} />
            <Route path="/payment/success" element={<RequireAuth><PaymentResult success /></RequireAuth>} />
            <Route path="/payment/cancel" element={<PaymentResult success={false} />} />
            <Route path="/app" element={<RequireAuth><AppShell /></RequireAuth>} />
            <Route path="/welcome" element={<RequireAuth><Welcome /></RequireAuth>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
